import Input from "../components/inputs/Input";
import { AiOutlineDashboard, AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Table,
  Tabs,
} from "flowbite-react";
import { useForm } from "react-hook-form";
import {
  ADMIN,
  API,
  AUDITS,
  CATEGORIES,
  PAYMENTS,
  TICKETS,
  USERS,
} from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { useApplication } from "../contexts/ApplicationContext";
import { useQuery, useQueryClient } from "react-query";
import {
  HiAdjustments,
  HiOutlineAnnotation,
  HiUserCircle,
} from "react-icons/hi";
import { useNavigate, useParams } from "react-router-dom";
import DrawerLoading from "../components/DrawerLoading";
import {
  MdAlignHorizontalLeft,
  MdOutlineIntegrationInstructions,
  MdOutlinePayments,
  MdOutlineSupportAgent,
  MdPeopleOutline,
} from "react-icons/md";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { GrDocumentTransfer } from "react-icons/gr";
import {
  IoDocumentOutline,
  IoPeopleSharp,
  IoSettingsOutline,
} from "react-icons/io5";
import EmptyTable from "../components/tables/EmptyTable";
import moment from "moment";
import { formatToReal } from "../utils/utils";
import { FaEdit, FaRegEdit, FaUserFriends } from "react-icons/fa";
import AdminUsersTableSpecific from "../components/tables/AdminUsersTableSpecific";
import AdminPaymentsTableSpecific from "../components/tables/AdminPaymentsTableSpecific";
import AdminReportSurveysTableSpecific from "../components/tables/AdminReportSurveysTableSpecific";
import AdminHistoryTableSpecific from "../components/tables/AdminHistoryTableSpecific";
import AdminCVsTableSpecific from "../components/tables/AdminCVsTableSpecific";
import { CopyBlock, dracula } from "react-code-blocks";
import AdminIntegrationSpecific from "../components/tables/AdminIntegrationSpecific";
import AdminAnnotationSpecific from "../components/tables/AdminAnnotationSpecific";
import TitleStripe from "../components/common/TitleStripe";
import Drawer from "../components/Drawer";
import CompanyEditForm from "../components/forms/CompanyEditForm";
import CurrencyInput from "../components/inputs/CurrencyInput";
import SelectInput from "../components/inputs/SelectInput";
import { BsFillBarChartFill } from "react-icons/bs";
import AdminHistoryGeneralTableSpecific from "../components/tables/AdminHistoryGeneralTableSpecific";
import AdminCustomersTableSpecific from "../components/tables/AdminCustomersTableSpecific";
import EditQueryCustomSpecificForm from "../components/forms/EditQueryCustomSpecificForm";
import { TbLockSearch } from "react-icons/tb";
import CompanyQueriesFormV2 from "../components/forms/CompanyQueriesFormV2";
import CompanyFilesManager from "./CompanyFilesManager";

const CompanyDetailsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);
  const { id } = useParams();
  const { userPermission } = useApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmittingAddCredit, setIsSubmittingAddCredit] = useState(false);
  const [errorAddCredit, setErrorAddCredit] = useState(null);
  const [openModalConfigQuery, setOpenModalConfigQuery] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);

  const {
    isLoading: isLoadingCompany,
    error: errorCompany,
    data: company,
  } = useQuery(["company"], async () => {
    if (id) {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${id}?type=id`
      );
      return data.data;
    }
    return null;
  });

  const {
    isLoading: isLoadingCompanyQueries,
    error: errorCompanyQueries,
    data: companyQueries,
    refetch,
  } = useQuery(
    ["companyQueries", company],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES}/${company?.id}`
      );
      return items.data;
    },
    {
      enabled: !!company?.id, // ✅ só roda quando company.id estiver definido
    }
  );

  const {
    isLoading: isLoadingOverview,
    error: errorOverview,
    data: overview,
  } = useQuery(
    ["overview", company?.id],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_OVERVIEW}/${company?.id}`);

      return data.data;
    },
    { enabled: !!company?.id }
  );

  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );

    return data.data.data;
  });

  const groupedQueries = useMemo(() => {
    return categories?.map((category) => ({
      ...category,
      queries:
        companyQueries?.filter((query) => query.categoryId === category.id) ||
        [],
    }));
  }, [categories, companyQueries]);

  const {
    isLoading: isLoadingTickets,
    error: errorListTickets,
    data: listTickets,
  } = useQuery(
    ["listTickets", company],
    async () => {
      try {
        const { data } = await axios.get(
          `${ADMIN.GET_TICKETS}?page=1&itemsPerPage=5&companyId=${company?.id}`
        );

        return data.data.data;
      } catch (error) {
        console.error("Erro ao buscar dados:", error.message);
        throw new Error("Erro ao buscar dados.");
      }
    },
    { enabled: !!company?.id }
  );

  const {
    isLoading: isLoadingInvoices,
    error: errorInvoices,
    data: invoices,
  } = useQuery(
    ["invoices", company?.id],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?page=${1}&itemsPerPage=6&companyId=${
          company?.id
        }`
      );

      return data.data.data;
    },
    { enabled: !!company?.id }
  );

  const {
    register: registerAddCredit,
    handleSubmit: handleSubmitAddCredit,
    reset: resetAddCredit,
    control: controlCredit,
    formState: { errors: errorsAddCredit },
  } = useForm();

  const onSubmitAddCredit = async (data, type = "add") => {
    try {
      setIsSubmittingAddCredit(true);

      const numericValue = +data?.value;

      if (numericValue <= 0) {
        throw new Error("Valor inválido!");
      }

      const value = type === "remove" ? numericValue * -1 : numericValue;

      const bodyData = {
        payment: {
          customer: company.id,
          value: value,
          methodPayment: data.methodPayment,
        },
      };

      await axios.post(`${PAYMENTS.ADD_CREDIT_MANUALLY}`, bodyData);

      queryClient.invalidateQueries(["company"]);
      queryClient.invalidateQueries(["invoices"]);
      queryClient.invalidateQueries(["overview"]);

      resetAddCredit();
      setErrorAddCredit(null);
    } catch (error) {
      setErrorAddCredit(error);
      console.error(error);
    } finally {
      setIsSubmittingAddCredit(false);
    }
  };

  const statusNum = Number(company?.status);
  const STATUS_STYLES = {
    1: {
      gradient: "from-green-400 to-green-600",
      badge: "success",
      label: "Ativo",
    },
    0: {
      gradient: "from-red-400 to-red-600",
      badge: "failure",
      label: "Bloqueado",
    },
    // default (inadimplente / qualquer outro valor)
    "-1": {
      gradient: "from-red-400 to-red-600",
      badge: "failure",
      label: "Inadimplente",
    },
  };
  const style = STATUS_STYLES[statusNum] || STATUS_STYLES["-1"];

  const fmtBR = (d) =>
    d && moment(d).isValid() ? moment(d).format("DD/MM/YYYY") : "—";
  const daysSince = (d) =>
    d && moment(d).isValid() ? moment().diff(moment(d), "days") : null;
  const daysUntil = (d) =>
    d && moment(d).isValid() ? moment(d).diff(moment(), "days") : null;

  // dentro do componente, antes do JSX:
  const createdDays = daysSince(company?.createdAt);
  const endDays = daysUntil(company?.dateEndContract);

  return (
    <>
      <Button
        onClick={() => navigate(-1)}
        size="xs"
        className="w-fit bg-gray-500 px-6 mb-2"
      >
        Voltar
      </Button>
      <div className="bg-white px-4 py-4 rounded-md shadow-md relative">
        {/* <DrawerLoading /> */}
        <Tabs
          aria-label="Default tabs"
          id="tab-company-details"
          activeTab={activeTab}
          onActiveTabChange={(tabIndex) => setActiveTab(tabIndex)}
        >
          <Tabs.Item title="Resumo" icon={AiOutlineDashboard}>
            {/* ====== HEADER + AÇÕES RÁPIDAS ====== */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
              <div>
                <div className="text-xl font-semibold text-gray-900">
                  {company?.companyTypeId === "REPRESENTANTE"
                    ? "Representante"
                    : "Cliente"}{" "}
                  · {company?.name || company?.socialReason}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Representante Pai:</span>{" "}
                  {company?.ParentCompany?.name ||
                    company?.ParentCompany?.socialReason ||
                    "—"}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="xs"
                  color="light"
                  onClick={() => setIsDrawerEditCustomer(true)}
                >
                  <FaEdit className="mr-1" /> Editar
                </Button>
                {company?.typePayment === "PRE" && (
                  <>
                    <Button
                      size="xs"
                      className="primary"
                      onClick={() => {
                        // foca no input do formulário de crédito na própria página (se preferir, abra modal)
                        const el = document.querySelector('[name="value"]');
                        el?.focus();
                      }}
                    >
                      <MdOutlinePayments className="mr-1" /> Adicionar crédito
                    </Button>
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => {
                        const el = document.querySelector('[name="value"]');
                        el?.focus();
                      }}
                    >
                      Remover crédito
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {/* Inadimplente */}
              {company?.status === 2 && (
                <Alert color="failure">
                  Cliente <b>inadimplente</b>. Algumas funções podem estar
                  restritas.
                </Alert>
              )}

              {/* Baixo crédito (pré-pago) */}
              {company?.typePayment === "PRE" &&
                Number(company?.credit || 0) <= 45 && (
                  <Alert color="warning">
                    Crédito baixo: {formatToReal(company?.credit || 0)}.
                    Considere adicionar crédito.
                  </Alert>
                )}

              {/* Sem nenhuma consulta ativa */}
              {Array.isArray(companyQueries) && companyQueries.length === 0 && (
                <Alert color="gray">
                  Nenhuma consulta ativa neste cliente. Configure em “Tabela de
                  consultas”.
                </Alert>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
              <div
                className={`p-6 rounded-xl shadow-md text-white bg-gradient-to-br ${style.gradient}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium opacity-90">Status</span>
                  <Badge color={style.badge} size="xs">
                    {statusNum === 1 ? "OK" : "Atenção"}
                  </Badge>
                </div>

                <div className="mt-3 text-2xl font-bold">{style.label}</div>

                <div className="mt-1 text-xs opacity-90">
                  Plano:{" "}
                  <b>
                    {company?.typePayment === "PRE" ? "Pré-pago" : "Pós-pago"}
                  </b>
                </div>
              </div>

              {/* Crédito / VCM */}
              <div className="p-6 rounded-xl shadow-md bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <span className="text-sm font-medium opacity-90">
                  💳 Crédito / VCM
                </span>
                <div className="mt-3 text-2xl font-bold">
                  {company?.typePayment === "PRE"
                    ? formatToReal(company?.credit || 0)
                    : `VCM: ${formatToReal(company?.vcm || 0)}`}
                </div>
                <div className="mt-1 text-xs opacity-80">
                  {company?.typePayment === "PRE"
                    ? "Saldo disponível"
                    : "Limite mensal (pós)"}
                </div>
              </div>

              {/* Total Utilizado */}
              <div className="p-6 rounded-xl shadow-md bg-gradient-to-br from-pink-400 to-red-500 text-white">
                <span className="text-sm font-medium opacity-90">
                  📊 Total utilizado
                </span>
                <div className="mt-3 text-2xl font-bold">
                  {formatToReal(overview?.grandTotal || 0)}
                </div>
                <div className="mt-1 text-xs opacity-80">
                  Consultas, laudos e comunicados
                </div>
              </div>
            </div>

            {/* ====== COLUNAS: DADOS RÁPIDOS • TIMELINE • CONSULTAS ATIVAS ====== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              {/* 1) Dados Rápidos + Acesso */}
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4 text-sm text-gray-700">
                {isLoadingCompany && <DrawerLoading />}
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  🧾 Dados rápidos
                </h2>

                <div className="space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">Nome/Razão Social</p>
                    <p className="font-medium break-words">
                      {company?.name || company?.socialReason || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Documento</p>
                    <p className="font-medium break-words">
                      {company?.document || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Telefone</p>
                    <p className="font-medium break-words">
                      {company?.phoneNumber || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">E-mail</p>
                    <p className="font-medium break-words">
                      {company?.email || "—"}
                    </p>
                  </div>
                  {company?.financeEmail ? (
                    <div>
                      <p className="text-xs text-gray-500">
                        E-mail do Financeiro
                      </p>
                      <p className="font-medium break-words">
                        {company?.financeEmail || "—"}
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <p className="text-xs text-gray-500">Cliente desde:</p>
                    <p className="font-medium break-words">
                      {fmtBR(company?.createdAt)}
                      {createdDays !== null && (
                        <span className="ml-2 text-xs text-gray-500">
                          {createdDays === 0
                            ? "(hoje)"
                            : createdDays === 1
                            ? "(há 1 dia)"
                            : `(há ${createdDays} dias)`}
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">
                      Data de término do contrato:
                    </p>
                    <p className="font-medium break-words">
                      {fmtBR(company?.dateEndContract)}
                      {endDays !== null && (
                        <span
                          className={`ml-2 text-xs ${
                            endDays < 0 ? "text-red-600" : "text-gray-500"
                          }`}
                        >
                          {endDays === 0
                            ? "(hoje)"
                            : endDays > 0
                            ? `(${endDays} ${
                                endDays === 1 ? "dia" : "dias"
                              } restantes)`
                            : `(vencido há ${Math.abs(endDays)} ${
                                Math.abs(endDays) === 1 ? "dia" : "dias"
                              })`}
                        </span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500">Termos aceitos em:</p>
                    <p className="font-medium break-words">
                      {company?.termsAcceptedAt ? (
                        moment(company?.termsAcceptedAt)?.format(
                          "DD/MM/yyyy HH:MM"
                        )
                      ) : (
                        <span className="text-red-500 font-semibold bg-red-50 px-1 rounded-md">
                          Não aceito
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Plano contratado:</p>
                    <p className="font-medium break-words">
                      {company?.typePayment === "PRE" ? "Pré-Pago" : "Pós-Pago"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      Mostrar logo nas consultas?
                    </p>
                    <p className="font-medium break-words">
                      {company?.showLogo ? "Sim" : "Não"}
                    </p>
                  </div>
                </div>
              </section>

              {/* 2) Timeline: Pagamentos + Chamados */}
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                {(isLoadingInvoices || isLoadingTickets) && <DrawerLoading />}
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  🕓 Últimas movimentações
                </h2>

                {(!invoices || invoices.length === 0) &&
                (!listTickets || listTickets.length === 0) ? (
                  <Alert color="gray">Sem movimentações recentes.</Alert>
                ) : (
                  <ul className="space-y-3">
                    {/* Pagamentos (até 4) */}
                    {invoices?.slice(0, 4).map((p, i) => (
                      <li
                        key={`pay-${i}`}
                        className="border rounded-md p-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">
                            {p.isPaid ? "Pagamento" : "Cobrança"} •{" "}
                            {formatToReal(p.price)}
                          </div>
                          <Badge
                            color={p.isPaid ? "success" : "warning"}
                            size="xs"
                          >
                            {p.isPaid ? "Pago" : "Pendente"}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {moment(p.createdAt).format("DD/MM/yyyy [às] HH:mm")}{" "}
                          • {p.type}
                        </div>
                        <div
                          className="text-xs text-gray-500 truncate"
                          title={p.creator?.name}
                        >
                          Criado por: {p.creator?.name || "—"}
                        </div>
                      </li>
                    ))}

                    {/* Chamados (até 4) */}
                    {listTickets?.slice(0, 4).map((t, i) => (
                      <li key={`tic-${i}`} className="border rounded-md p-3">
                        <div className="text-2xs uppercase text-gray-500">
                          {t?.status === 0 ? "Pendente" : "Finalizado"} •{" "}
                          {moment(t?.createdAt).format("DD/MM/yyyy")}
                        </div>
                        <div className="font-semibold">
                          {t?.id} - {t?.title}
                        </div>
                        <div className="text-sm text-gray-600 line-clamp-3">
                          {t?.description}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* 3) Consultas ativas com busca inline */}
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4 relative">
                {(isLoadingCompanyQueries || isLoadingCategories) && (
                  <DrawerLoading />
                )}

                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  📄 Consultas ativas
                </h2>

                {/* Busca local */}
                <div className="mb-2">
                  <input
                    placeholder="Buscar consulta por nome…"
                    onChange={(e) => {
                      const q = (e.target.value || "").toLowerCase();
                      const rows = document.querySelectorAll(".query-row");
                      rows.forEach((li) => {
                        const name = (
                          li.getAttribute("data-name") || ""
                        ).toLowerCase();
                        li.style.display = name.includes(q) ? "" : "none";
                      });
                    }}
                    className="bg-gray-50 rounded-md w-full p-2 border border-gray-300"
                  />
                </div>

                <div
                  className="overflow-y-auto pr-2"
                  style={{ maxHeight: 320 }}
                >
                  {(() => {
                    const grouped = (categories || []).map((category) => {
                      const items = (companyQueries || []).filter(
                        (q) => q.categoryId === category.id
                      );
                      const mains = items.filter((q) => !q.isAdditional);
                      const adds = items.filter((q) => !!q.isAdditional);
                      return { ...category, mains, adds };
                    });

                    const any = grouped.some(
                      (g) => (g.mains?.length || 0) + (g.adds?.length || 0) > 0
                    );
                    if (!any)
                      return (
                        <Alert color="gray">Nenhuma consulta contratada.</Alert>
                      );

                    return (
                      <div className="space-y-4">
                        {grouped.map((cat) => (
                          <div key={cat.id} className="space-y-2">
                            <div className="font-semibold text-green-600 uppercase text-xs">
                              {cat.name}
                            </div>

                            {/* PRINCIPAIS */}
                            <div className="rounded-lg border border-gray-100">
                              <div className="px-2 py-1.5 bg-gray-50 border-b border-gray-100 rounded-t-lg">
                                <span className="text-[11px] font-semibold text-gray-600 tracking-wide">
                                  Consultas principais
                                </span>
                              </div>

                              {cat.mains?.length ? (
                                <ul className="divide-y">
                                  {cat.mains.map((q) => (
                                    <li
                                      key={`main-${q.id}`}
                                      className="query-row py-2 px-2 flex items-center justify-between gap-2"
                                      data-name={q?.name || ""}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-sm truncate">
                                          {q?.name}
                                        </span>
                                        {q?.QueryCustomizedId && (
                                          <Badge size="xs" color="info">
                                            Custom
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 shrink-0">
                                        <span className="text-sm font-medium">
                                          {formatToReal(q?.price)}
                                        </span>
                                        {userPermission === "POWER_USER" && (
                                          <IoSettingsOutline
                                            className="cursor-pointer text-gray-600 hover:text-gray-900"
                                            onClick={() => {
                                              setSelectedQuery(q);
                                              setOpenModalConfigQuery(true);
                                            }}
                                            title="Configurar"
                                          />
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-gray-500 px-2 py-2">
                                  Sem itens principais nesta categoria.
                                </div>
                              )}
                            </div>

                            {/* ADICIONAIS */}
                            <div className="rounded-lg border border-gray-100">
                              <div className="px-2 py-1.5 bg-emerald-50/60 border-b border-gray-100 rounded-t-lg">
                                <span className="text-[11px] font-semibold text-emerald-700 tracking-wide">
                                  Consultas adicionais
                                </span>
                              </div>

                              {cat.adds?.length ? (
                                <ul className="divide-y">
                                  {cat.adds.map((q) => (
                                    <li
                                      key={`add-${q.id}`}
                                      className="query-row py-2 px-2 flex items-center justify-between gap-2"
                                      data-name={q?.name || ""}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-sm truncate">
                                          {q?.name}
                                        </span>

                                        {q?.QueryCustomizedId && (
                                          <Badge size="xs" color="info">
                                            Custom
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 shrink-0">
                                        <span className="text-sm font-medium">
                                          {formatToReal(q?.price)}
                                        </span>
                                        {userPermission === "POWER_USER" && (
                                          <IoSettingsOutline
                                            className="cursor-pointer text-gray-600 hover:text-gray-900"
                                            onClick={() => {
                                              setSelectedQuery(q);
                                              setOpenModalConfigQuery(true);
                                            }}
                                            title="Configurar"
                                          />
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <div className="text-xs text-gray-500 px-2 py-2">
                                  Sem adicionais nesta categoria.
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </section>
            </div>
            <div className="mt-4">
              <AdminIntegrationSpecific
                companyId={company?.id}
                isActive={true}
              />
            </div>

            {/* ====== CRÉDITO (PRÉ-PAGO) – mantém seu formulário atual ====== */}
            {company?.typePayment === "PRE" && (
              <section className="bg-white rounded-xl border border-gray-200 shadow-md p-4 mt-4">
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  💳 Gerenciar Crédito
                </h2>
                {/* mantém o seu form existente */}
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-3"
                >
                  <CurrencyInput
                    label="Valor"
                    name="value"
                    register={registerAddCredit}
                    control={controlCredit}
                  />
                  <SelectInput
                    register={registerAddCredit}
                    label="Método de pagamento"
                    name="methodPayment"
                  >
                    <option value="PIX">Pix</option>
                    <option value="BOLETO">Boleto</option>
                    <option value="CARTAO">Cartão de crédito</option>
                  </SelectInput>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      className="primary"
                      isProcessing={isSubmittingAddCredit}
                      processingSpinner={
                        <AiOutlineLoading className="h-5 w-5 animate-spin" />
                      }
                      onClick={handleSubmitAddCredit((data) =>
                        onSubmitAddCredit(data, "add")
                      )}
                    >
                      Adicionar crédito
                    </Button>
                    <Button
                      color="failure"
                      isProcessing={isSubmittingAddCredit}
                      processingSpinner={
                        <AiOutlineLoading className="h-5 w-5 animate-spin" />
                      }
                      onClick={handleSubmitAddCredit((data) =>
                        onSubmitAddCredit(data, "remove")
                      )}
                    >
                      Remover crédito
                    </Button>
                  </div>
                  {errorAddCredit && (
                    <Alert color="failure">
                      Erro:{" "}
                      {String(errorAddCredit?.message || "Falha ao processar")}
                    </Alert>
                  )}
                </form>
              </section>
            )}
          </Tabs.Item>
          {/* <Tabs.Item title="Consumo Geral" icon={MdAlignHorizontalLeft}>
            <AdminHistoryGeneralTableSpecific
              companyId={company?.id}
              isActive={activeTab === 1}
            />
          </Tabs.Item> */}
          <Tabs.Item title="Pagamentos" icon={MdOutlinePayments}>
            <AdminPaymentsTableSpecific
              companyId={company?.id}
              isActive={activeTab === 1}
            />
          </Tabs.Item>
          <Tabs.Item title="Consultas" icon={FaMagnifyingGlass}>
            <AdminHistoryTableSpecific
              companyId={company?.id}
              isActive={activeTab === 2}
            />
          </Tabs.Item>

          <Tabs.Item title="Permissões" icon={TbLockSearch}>
            {/* Renderiza somente quando a aba está ativa para evitar fetch desnecessário */}
            {activeTab === 3 && (
              <div className="relative">
                <CompanyQueriesFormV2
                  company={company}
                  categories={categories}
                  onClose={() => {
                    /* noop: mantemos na aba */
                  }}
                  fetchCompanies={() => {
                    // Invalida os dados que refletem alterações de permissões e preços
                    queryClient.invalidateQueries(["companyQueries"]);
                    queryClient.invalidateQueries(["overview"]);
                    queryClient.invalidateQueries(["categories"]);
                    // Se você listar consultas ativas/tabelas em outras abas:
                    queryClient.invalidateQueries(["invoices"]);
                  }}
                />
              </div>
            )}
          </Tabs.Item>
          {/* <Tabs.Item title="Laudos" icon={IoDocumentOutline}>
            <AdminReportSurveysTableSpecific
              companyId={company?.id}
              isActive={activeTab === 4}
            />
          </Tabs.Item>
          <Tabs.Item title="Comunicados" icon={GrDocumentTransfer}>
            <AdminCVsTableSpecific
              companyId={company?.id}
              isActive={activeTab === 5}
            />
          </Tabs.Item> */}
          <Tabs.Item
            title={`Usuários (${overview?.user?.totalCount})`}
            icon={FaUserFriends}
          >
            <AdminUsersTableSpecific
              companyId={company?.id}
              isActive={activeTab === 4}
            />
          </Tabs.Item>
          <Tabs.Item
            title={`Anotações (${overview?.annotation?.totalCount})`}
            icon={HiOutlineAnnotation}
          >
            <AdminAnnotationSpecific
              companyId={company?.id}
              isActive={activeTab === 5}
            />
          </Tabs.Item>
          <Tabs.Item title="Arquivos" icon={IoDocumentOutline}>
            <CompanyFilesManager
              companyId={company?.id}
              accept="application/pdf,image/*"
              maxSizeMB={25}
              isActive={activeTab === 6}
            />
          </Tabs.Item>
          {company?.companyTypeId === "REPRESENTANTE" && (
            <Tabs.Item title="Clientes" icon={MdPeopleOutline}>
              <AdminCustomersTableSpecific
                companyId={company?.id}
                isActive={activeTab === 7}
              />
            </Tabs.Item>
          )}
        </Tabs>
        <Modal
          show={isDrawerEditCustomer}
          onClose={() => setIsDrawerEditCustomer(false)}
          size="5xl"
        >
          <ModalHeader>Editar cliente</ModalHeader>
          <ModalBody>
            <CompanyEditForm
              company={company}
              onClose={setIsDrawerEditCustomer}
              categories={categories}
              fetchCompanies={() => queryClient.invalidateQueries(["company"])}
              isOpen={isDrawerEditCustomer}
            />
          </ModalBody>
        </Modal>
      </div>

      <Modal
        show={openModalConfigQuery}
        onClose={() => setOpenModalConfigQuery(false)}
        size="4xl"
      >
        <ModalHeader>Configurar consulta</ModalHeader>
        <ModalBody>
          <EditQueryCustomSpecificForm
            query={selectedQuery}
            company={company}
            setOpenModal={setOpenModalConfigQuery}
            fetchCompanyQueries={() => {
              queryClient.invalidateQueries(["companyQueries"]);
              queryClient.invalidateQueries(["categories"]);
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default CompanyDetailsPage;
